// /badges-lab (admins): a bench for the badge art. Every category at a few tiers,
// full and mini, idle motion, locked, the count chip, the sealed state, the reveal
// with replay, the fallback path and the share-image helper. Other badge work adds
// its own sections here.
import React, { useMemo, useState } from "react";
import { Button } from "../../ui";
import { TIERS, type Tier } from "./types";
import {
  BadgeArt,
  BADGE_REVEAL_MS,
  getBadgeSvg,
  svgToDataUrl,
  useBadgeLib,
  type BadgeArtAnimate,
  type BadgeLib,
} from "./art";
import RevealLab from "./reveal/RevealLab";

const LAB_TIERS: Tier[] = ["PAPER", "ROCK", "SILVER", "GOLD", "RUBY", "DIAMOND"];
const FAMILY_ORDER = ["aotd", "race", "coffee", "kitchen", "chat", "cards", "social", "community", "prints", "account"];

const panel: React.CSSProperties = { border: "var(--hm-border)", boxShadow: "var(--hm-shadow)" };

function sampleNumber(lib: BadgeLib, category: string): number | null {
  const own = lib.badges.filter((b) => b.category === category);
  if (!own.length) return 10;
  const mid = own[Math.floor(own.length / 2)];
  return mid.number ?? null;
}

function Section({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section className="mb-8 p-4 bg-white text-black" style={panel}>
      <h2 className="text-lg font-bold mb-1">{title}</h2>
      {note && <p className="text-sm text-gray-600 mb-3">{note}</p>}
      {children}
    </section>
  );
}

function Caption({ children }: { children: React.ReactNode }) {
  return <div className="text-[11px] leading-tight text-gray-600 mt-1 text-center max-w-[110px]">{children}</div>;
}

const BadgeLab: React.FC = () => {
  const { lib, error, loading } = useBadgeLib();
  const [motion, setMotion] = useState<BadgeArtAnimate>("idle");
  const [dark, setDark] = useState(false);
  const [count, setCount] = useState(1);
  const [revealKey, setRevealKey] = useState(0);
  const [revealDone, setRevealDone] = useState(false);
  const [sealed, setSealed] = useState(true);
  const [pickCategory, setPickCategory] = useState("AOTD_COUNT");
  const [pickTier, setPickTier] = useState<Tier>("RUBY");
  const [shareImg, setShareImg] = useState<{ url: string; bytes: number } | null>(null);
  const [shareNote, setShareNote] = useState("");

  const categories = useMemo(() => {
    if (!lib) return [];
    return Object.entries(lib.categories).sort(([a, ca], [b, cb]) => {
      const fa = FAMILY_ORDER.indexOf(ca.family);
      const fb = FAMILY_ORDER.indexOf(cb.family);
      return (fa < 0 ? 99 : fa) - (fb < 0 ? 99 : fb) || a.localeCompare(b);
    });
  }, [lib]);

  const pickNumber = lib ? sampleNumber(lib, pickCategory) : 10;
  const replay = () => {
    setRevealDone(false);
    setRevealKey((k) => k + 1);
  };

  const renderShare = async () => {
    setShareNote("");
    const svg = await getBadgeSvg(
      { category: pickCategory, tier: pickTier, number: pickNumber, count, variant: "full" },
      { pixelSize: 1080 }
    ).catch(() => null);
    if (!svg) {
      setShareImg(null);
      setShareNote("The library cannot draw this badge, so the share image would use the fallback.");
      return;
    }
    setShareImg({ url: svgToDataUrl(svg), bytes: svg.length });
  };

  const stageBg = dark ? "#1b1b1b" : "#f4f1e8";

  return (
    <div className="min-h-screen px-4 py-6 md:px-8" style={{ background: stageBg }}>
      <div className="max-w-6xl mx-auto">
        <header className="mb-6 p-4 bg-white" style={panel}>
          <h1 className="text-2xl font-bold">Badge lab</h1>
          <p className="text-sm text-gray-700">
            Art from the parts library ({lib ? `${Object.keys(lib.categories).length} categories, ${Object.keys(lib.parts).length} parts` : loading ? "loading" : "not loaded"}).
            Refresh it with <code>pnpm badges:sync-art</code>.
          </p>
          {error && <p className="text-sm text-red-700 mt-1">The library did not load: {error.message}. Badges show their fallback.</p>}
          <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
            <label className="flex items-center gap-1">
              Motion
              <select className="border-2 border-black px-1" value={motion} onChange={(e) => setMotion(e.target.value as BadgeArtAnimate)}>
                <option value="idle">idle</option>
                <option value="none">still</option>
              </select>
            </label>
            <label className="flex items-center gap-1">
              Count
              <select className="border-2 border-black px-1" value={count} onChange={(e) => setCount(Number(e.target.value))}>
                {[1, 2, 3, 12, 100].map((n) => (
                  <option key={n} value={n}>
                    {n === 1 ? "single" : `x${n}`}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={dark} onChange={(e) => setDark(e.target.checked)} />
              Dark page
            </label>
          </div>
        </header>

        <Section title="Reveal" note={`Replay restarts the layer animation (${BADGE_REVEAL_MS} ms). With reduced motion it is a plain fade.`}>
          <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
            <select
              className="border-2 border-black px-1"
              value={pickCategory}
              onChange={(e) => {
                setPickCategory(e.target.value);
                replay();
              }}
            >
              {categories.map(([key, c]) => (
                <option key={key} value={key}>
                  {c.name} ({key})
                </option>
              ))}
            </select>
            <select
              className="border-2 border-black px-1"
              value={pickTier}
              onChange={(e) => {
                setPickTier(e.target.value as Tier);
                replay();
              }}
            >
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <Button type="button" size="sm" onClick={replay}>
              Replay reveal
            </Button>
            <span className="text-gray-600">{revealDone ? "Done" : "Playing"}</span>
          </div>
          <div className="flex flex-wrap items-end gap-8 p-6" style={{ background: stageBg }}>
            <BadgeArt
              category={pickCategory}
              tier={pickTier}
              number={pickNumber}
              count={count}
              size={240}
              variant="full"
              animate="reveal"
              revealKey={revealKey}
              onRevealEnd={() => setRevealDone(true)}
            />
            <BadgeArt
              category={pickCategory}
              tier={pickTier}
              number={pickNumber}
              count={count}
              size={64}
              variant="mini"
              animate="reveal"
              revealKey={revealKey}
            />
          </div>
        </Section>

        <Section title="Sealed" note="The cinematic's first state: only the base, as a dark silhouette that shakes. Breaking the seal plays the reveal.">
          <div className="flex items-center gap-6">
            <div className="p-6" style={{ background: "#111" }}>
              <BadgeArt
                category={pickCategory}
                tier={pickTier}
                number={pickNumber}
                size={180}
                variant="full"
                sealed={sealed}
                animate={sealed ? "none" : "reveal"}
              />
            </div>
            <Button type="button" size="sm" onClick={() => setSealed((s) => !s)}>
              {sealed ? "Break the seal" : "Seal it again"}
            </Button>
          </div>
        </Section>

        <Section
          title="Earning a badge"
          note="Pick a badge, a tier, first on the site and which copy, then Earn it: it plays at the level the tier gives (Paper, Rock, Bronze a tile; Silver, Gold a card; the gems a cinematic; first on the site one step up, a repeat one down). The level buttons force one. Everything plays through the real reveal host view, worst to best. The share story underneath shows the same settings. Nothing is sent; the log shows what would be."
        >
          <RevealLab />
        </Section>

        <Section title="Every category" note={`Full at 96 px and mini at 32 px, at ${LAB_TIERS.join(", ").toLowerCase()}. The number is a sample from the catalogue.`}>
          {!lib && <p className="text-sm">{loading ? "Loading the badge library." : "No library, so nothing to list."}</p>}
          <div className="overflow-x-auto">
            <table className="text-sm border-collapse">
              <tbody>
                {categories.map(([key, c]) => {
                  const n = lib ? sampleNumber(lib, key) : null;
                  return (
                    <tr key={key} className="border-t-2 border-black align-middle">
                      <td className="pr-4 py-2 min-w-[160px]">
                        <div className="font-bold">{c.name}</div>
                        <div className="text-[11px] text-gray-600">
                          {key} · {c.family}
                          {c.repeatable ? " · repeatable" : ""}
                        </div>
                      </td>
                      {LAB_TIERS.map((t) => (
                        <td key={t} className="px-2 py-2" style={{ background: stageBg }}>
                          <div className="flex items-end gap-1">
                            <BadgeArt category={key} tier={t} number={n} count={count} size="lg" variant="full" animate={motion} name={c.name} />
                            <BadgeArt category={key} tier={t} number={n} count={count} size="sm" variant="mini" name={c.name} />
                          </div>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="Locked" note="Not earned yet: the README's grayscale filter, no motion. The collection shows the next tier like this.">
          <div className="flex flex-wrap items-end gap-4">
            {(["BRONZE", "GOLD", "EMERALD"] as Tier[]).map((t) => (
              <div key={t} className="flex items-end gap-1">
                <BadgeArt category="COFFEE_RATE_COUNT" tier={t} number={25} size="lg" variant="full" animate="idle" locked />
                <BadgeArt category="COFFEE_RATE_COUNT" tier={t} number={25} size="sm" variant="mini" locked />
              </div>
            ))}
          </div>
        </Section>

        <Section title="Count chip" note="Repeatable badges show how many copies the member holds.">
          <div className="flex flex-wrap items-end gap-4">
            {[2, 5, 12, 100].map((n) => (
              <div key={n} className="flex flex-col items-center">
                <div className="flex items-end gap-1">
                  <BadgeArt category="AOTD_STREAK" tier="SILVER" number={7} count={n} size="lg" variant="full" animate={motion} />
                  <BadgeArt category="AOTD_STREAK" tier="SILVER" number={7} count={n} size="sm" variant="mini" />
                </div>
                <Caption>x{n}</Caption>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Same badge twice" note="Each instance prefixes its own ids, so the glow filter of one never points at the other.">
          <div className="flex items-end gap-4">
            <BadgeArt category="AOTD_COUNT" tier="DIAMOND" number={365} size="xl" variant="full" animate={motion} />
            <BadgeArt category="AOTD_COUNT" tier="DIAMOND" number={365} size="xl" variant="full" animate={motion} />
            <BadgeArt category="AOTD_COUNT" tier="DIAMOND" number={365} size="md" variant="full" locked />
          </div>
        </Section>

        <Section
          title="Fallback path"
          note="A category the library does not know: first the old image from GCS (needs a session), then a tier-coloured tile with the name's initials."
        >
          <div className="flex flex-wrap items-end gap-6">
            <div className="flex flex-col items-center">
              <BadgeArt category="LAB_UNKNOWN" code="AOTD_NUMBER_1" name="First AOTD ever" tier="RUBY" number={1} size="lg" variant="full" />
              <Caption>unknown category, code with GCS art</Caption>
            </div>
            <div className="flex flex-col items-center">
              <BadgeArt category="LAB_UNKNOWN" code="LAB_UNKNOWN_7" name="Mystery badge" tier="GOLD" number={7} size="lg" variant="full" />
              <Caption>no GCS art: initials</Caption>
            </div>
            <div className="flex flex-col items-center">
              <BadgeArt category="LAB_UNKNOWN" code="LAB_UNKNOWN_8" name="Night owl" tier="RUBY" number={8} size="lg" variant="full" animate="reveal" revealKey={revealKey} />
              <Caption>initials, reveal</Caption>
            </div>
            <div className="flex flex-col items-center">
              <BadgeArt category="LAB_UNKNOWN" code="LAB_UNKNOWN_9" name="Deep sea" tier="DIAMOND" number={9} size="sm" variant="mini" />
              <Caption>initials, mini</Caption>
            </div>
          </div>
        </Section>

        <Section title="Share image" note="composeBadgeSvg / getBadgeSvg with pixelSize 1080: the SVG the share page draws onto its canvas.">
          <div className="flex flex-wrap items-center gap-4">
            <Button type="button" size="sm" onClick={renderShare}>
              Render the picked badge at 1080 px
            </Button>
            {shareImg && (
              <div className="flex items-center gap-3">
                <img src={shareImg.url} alt="The picked badge at share size" width={180} height={180} style={panel} />
                <span className="text-sm text-gray-600">{(shareImg.bytes / 1024).toFixed(1)} KB of SVG</span>
              </div>
            )}
            {shareNote && <span className="text-sm text-gray-600">{shareNote}</span>}
          </div>
        </Section>
      </div>
    </div>
  );
};

export default BadgeLab;
